import { JourneyChangeSearchWidget } from '@/components/journey-change/JourneyChangeSearchWidget';
import JourneyCard from '@/components/purchase/journey/JourneyCard';
import JourneyCardSkeleton from '@/components/purchase/journey/JourneyCardSkeleton';
import { setSelectedOutboundJourney } from '@/features/purchase/purchaseActions';
import { useJourneyChangeDeepLink } from '@/hooks/useJourneyChangeDeepLink';
import { TransText } from '@/i18n/trans/text';
import type { SearchJourneyOffersDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { GTM_EVENT } from '@/utils/gtm';
import { journeyChangePath } from '@/utils/journeyChangeUrlParams';
import { getJourneyWarning } from '@/utils/warning';
import { Typography } from '@packages/webshop-design-system';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';

const JourneyCardSkeletonList = () => (
  <div className="grid grid-cols-[1fr_auto] gap-y-4">
    {[1, 2, 3, 4].map((i) => (
      <JourneyCardSkeleton key={i} />
    ))}
  </div>
);

const JourneySelect = () => {
  const { isLoading, isSearching, searchOffers, searchFormValues, search } =
    useJourneyChangeDeepLink();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  const navigate = useNavigate();
  const location = useLocation();

  // The deep-link params stay on the query string so the rest of the flow can
  // drive the token-scoped exchange itself.
  const goToOfferSelect = (journey: SearchJourneyOffersDTO) =>
    navigate(journeyChangePath(location.search, 'offer'), {
      state: { journey },
    });

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-laptop p-4">
        <div className="mx-auto max-w-mobile">
          <JourneyCardSkeletonList />
        </div>
      </div>
    );
  }

  const journeys = searchOffers?.journeys ?? [];

  return (
    <>
      <JourneyChangeSearchWidget
        formValues={searchFormValues}
        isSearching={isSearching}
        onSearch={search}
      />
      <div className="mx-auto w-full max-w-laptop p-4">
        <div className="mx-auto max-w-mobile" data-testid="journey-change-list">
          <Typography
            variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
            className="mb-4"
            as="h1"
          >
            <TransText i18nKey="selectOutboundTrip" />
          </Typography>
          {isSearching ? (
            <JourneyCardSkeletonList />
          ) : journeys.length > 0 ? (
            <div className="grid grid-cols-[1fr_auto] gap-y-4">
              {journeys.map((journey) => (
                <JourneyCard
                  key={journey.reference}
                  journey={journey}
                  warning={getJourneyWarning(journey, searchOffers?.warnings)}
                  onContinue={() => goToOfferSelect(journey)}
                  setSelectedJourney={setSelectedOutboundJourney}
                  journeySelectedEvent={GTM_EVENT.OUTBOUND_JOURNEY_SELECTED}
                />
              ))}
            </div>
          ) : (
            <Typography variant="body1" className="text-neutral">
              <TransText i18nKey="noTripsFoundPleaseChangeSearchInputsAndTryAgain" />
            </Typography>
          )}
        </div>
      </div>
    </>
  );
};

export default JourneySelect;
